import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Table, ProgressBar } from 'react-bootstrap';
import { FaUsers, FaUndo, FaDownload } from 'react-icons/fa';

const AssignmentBulkOperations = ({ show, onHide, onRefresh }) => {
    const [activeTab, setActiveTab] = useState('assign');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [bulkAssignments, setBulkAssignments] = useState([{ assetId: '', employeeId: '', saralAssetId: '', serialNumber: '', notes: '' }]);
    const [bulkReturns, setBulkReturns] = useState([{ assignmentId: '', notes: '' }]);

    const handleBulkAssign = async () => {
        setLoading(true);
        try {
            const response = await fetch('/api/bulkoperations/bulk-assign', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(bulkAssignments.filter(a => a.assetId && a.employeeId))
            });

            const data = await response.json();
            setResult(data);
            if (data.successCount > 0 && onRefresh) onRefresh();
        } catch (error) {
            setResult({ successCount: 0, errorCount: 1, errors: [error.message] });
        } finally {
            setLoading(false);
        }
    };

    const handleBulkReturn = async () => {
        setLoading(true);
        try {
            const response = await fetch('/api/bulkoperations/bulk-return', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(bulkReturns.filter(r => r.assignmentId))
            });

            const data = await response.json();
            setResult(data);
            if (data.successCount > 0 && onRefresh) onRefresh();
        } catch (error) {
            setResult({ successCount: 0, errorCount: 1, errors: [error.message] });
        } finally {
            setLoading(false);
        }
    };

    const handleExportAssignments = async () => {
        try {
            const response = await fetch('/api/bulkoperations/export-assignments', {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'assignments.csv';
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            setResult({ successCount: 0, errorCount: 1, errors: [error.message] });
        }
    };

    const addAssignmentRow = () => {
        setBulkAssignments([...bulkAssignments, { assetId: '', employeeId: '', saralAssetId: '', serialNumber: '', notes: '' }]);
    };

    const addReturnRow = () => {
        setBulkReturns([...bulkReturns, { assignmentId: '', notes: '' }]);
    };

    const updateAssignment = (index, field, value) => {
        const updated = [...bulkAssignments];
        updated[index][field] = value;
        setBulkAssignments(updated);
    };

    const updateReturn = (index, field, value) => {
        const updated = [...bulkReturns];
        updated[index][field] = value;
        setBulkReturns(updated);
    };

    return (
        <Modal show={show} onHide={onHide} size="xl">
            <Modal.Header closeButton>
                <Modal.Title>Assignment Bulk Operations</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-3">
                    <Button 
                        variant={activeTab === 'assign' ? 'primary' : 'outline-primary'} 
                        className="me-2"
                        onClick={() => setActiveTab('assign')}
                    >
                        <FaUsers className="me-1" /> Bulk Assign
                    </Button>
                    <Button 
                        variant={activeTab === 'return' ? 'primary' : 'outline-primary'}
                        className="me-2"
                        onClick={() => setActiveTab('return')}
                    >
                        <FaUndo className="me-1" /> Bulk Return
                    </Button>
                    <Button 
                        variant="outline-success"
                        onClick={handleExportAssignments}
                    >
                        <FaDownload className="me-1" /> Export
                    </Button>
                </div>

                {result && (
                    <Alert variant={result.errorCount > 0 ? 'warning' : 'success'} className="mb-3">
                        <strong>Operation Complete:</strong> {result.successCount} successful, {result.errorCount} errors
                        {result.errors.length > 0 && (
                            <ul className="mt-2 mb-0">
                                {result.errors.slice(0, 5).map((error, index) => (
                                    <li key={index}>{error}</li>
                                ))}
                                {result.errors.length > 5 && <li>... and {result.errors.length - 5} more errors</li>}
                            </ul>
                        )}
                    </Alert>
                )}

                {loading && <ProgressBar animated now={100} className="mb-3" />}

                {activeTab === 'assign' && (
                    <div>
                        <h5>Bulk Asset Assignment</h5>
                        <Table striped bordered hover size="sm">
                            <thead>
                                <tr>
                                    <th>Asset ID</th>
                                    <th>Employee ID</th>
                                    <th>Saral Asset ID</th>
                                    <th>Serial Number</th>
                                    <th>Notes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bulkAssignments.map((assignment, index) => (
                                    <tr key={index}>
                                        <td>
                                            <Form.Control 
                                                size="sm"
                                                value={assignment.assetId}
                                                onChange={(e) => updateAssignment(index, 'assetId', e.target.value)}
                                                placeholder="Asset ID"
                                            />
                                        </td>
                                        <td>
                                            <Form.Control 
                                                size="sm"
                                                value={assignment.employeeId}
                                                onChange={(e) => updateAssignment(index, 'employeeId', e.target.value)}
                                                placeholder="Employee ID"
                                            />
                                        </td>
                                        <td>
                                            <Form.Control 
                                                size="sm"
                                                value={assignment.saralAssetId}
                                                onChange={(e) => updateAssignment(index, 'saralAssetId', e.target.value)}
                                                placeholder="Saral Asset ID"
                                            />
                                        </td>
                                        <td>
                                            <Form.Control 
                                                size="sm"
                                                value={assignment.serialNumber}
                                                onChange={(e) => updateAssignment(index, 'serialNumber', e.target.value)}
                                                placeholder="Serial Number"
                                            />
                                        </td>
                                        <td>
                                            <Form.Control 
                                                size="sm"
                                                value={assignment.notes}
                                                onChange={(e) => updateAssignment(index, 'notes', e.target.value)}
                                                placeholder="Notes"
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                        <div className="d-flex gap-2">
                            <Button variant="outline-primary" size="sm" onClick={addAssignmentRow}>
                                Add Row
                            </Button>
                            <Button variant="primary" onClick={handleBulkAssign} disabled={loading}>
                                Process Assignments
                            </Button>
                        </div>
                    </div>
                )}

                {activeTab === 'return' && (
                    <div>
                        <h5>Bulk Asset Return</h5>
                        <Table striped bordered hover size="sm">
                            <thead>
                                <tr>
                                    <th>Assignment ID</th>
                                    <th>Return Notes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bulkReturns.map((returnItem, index) => (
                                    <tr key={index}>
                                        <td>
                                            <Form.Control 
                                                size="sm"
                                                value={returnItem.assignmentId}
                                                onChange={(e) => updateReturn(index, 'assignmentId', e.target.value)}
                                                placeholder="Assignment ID"
                                            />
                                        </td>
                                        <td>
                                            <Form.Control 
                                                size="sm"
                                                value={returnItem.notes}
                                                onChange={(e) => updateReturn(index, 'notes', e.target.value)}
                                                placeholder="Return notes"
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                        <div className="d-flex gap-2">
                            <Button variant="outline-primary" size="sm" onClick={addReturnRow}>
                                Add Row
                            </Button>
                            <Button variant="warning" onClick={handleBulkReturn} disabled={loading}>
                                Process Returns
                            </Button>
                        </div>
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default AssignmentBulkOperations;